import React, { useMemo, useState, useCallback } from 'react';
import Card from './ui/Card';
import Button from './ui/Button';
import Table from './ui/Table';
import Spinner from './ui/Spinner';
import { rowPassesVizFilters } from '../utils/coringActivityHelpers';
import { exportToCSV } from '../utils/csvExport';
import './CoringDataExplorer.css';

/** v_coring column order aligned with python-api/coring_activity SELECT (stable CSV headers). */
const CORING_CSV_KEYS = [
  'report_date',
  'drill',
  'shift',
  'dsr_activity_id',
  'uid',
  'daily_report_id',
  'hole_id',
  'hole_name',
  'activity',
  'activity_type',
  'bit_size',
  'bit_size_id',
  'billable',
  'billing_type',
  'charge_from',
  'charge_to',
  'total_charges',
  'currency_code',
  'comments',
  'distance',
  'distance_drilled_from',
  'distance_drilled_to',
  'distance_unit_abbr',
  'distance_from_to_unit_abbr',
  'depth',
  'depth_unit_abbr',
  'activity_hours',
  'total_man_hours',
  'penetration',
  'work_sub_category_id',
  'work_sub_category_type_id',
  'data_deleted',
  'deleted_flag',
  'export_datetime',
  'last_seen_at'
];

function coringRowToCSVRecord(row) {
  const out = {};
  for (const key of CORING_CSV_KEYS) {
    const v = row[key];
    if (v == null || v === '') {
      out[key] = '';
    } else if (typeof v === 'object' && !(v instanceof Date)) {
      out[key] = JSON.stringify(v);
    } else {
      out[key] = v;
    }
  }
  return out;
}

const CHIP_MAX = 24;

/**
 * Read-only explorer: API response from krux.v_coring, active filters, inclusion vs chart KPIs.
 */
const CoringDataExplorer = ({
  data = [],
  loading = false,
  error = null,
  dateParams = {},
  dateRangeLabel = 'month',
  setDateRange,
  customStartDate = '',
  setCustomStartDate,
  customEndDate = '',
  setCustomEndDate,
  coringSearchTerm = '',
  setSearchTerm,
  holeFilters = [],
  filterContext = {},
  filteredHoles = []
}) => {
  const [holeListExpanded, setHoleListExpanded] = useState(false);

  const filterSummaryLines = useMemo(() => {
    const lines = [];
    const {
      statusFilters = {},
      filterType = 'status',
      zoneFilters = {},
      rigFilters = {},
      yearFilter = 'all',
      dashboardSearchTerm = '',
      selectedProjectId = 'all',
    } = filterContext;

    const activeStatuses = Object.keys(statusFilters).filter((k) => statusFilters[k]);
    if (activeStatuses.length) {
      lines.push({ label: 'Hole status (dashboard)', value: activeStatuses.join(', ') });
    }
    lines.push({ label: 'Dashboard filter mode', value: filterType });

    if (filterType === 'zone') {
      const z = Object.keys(zoneFilters).filter((k) => zoneFilters[k]);
      if (z.length) lines.push({ label: 'Zones', value: z.join(', ') });
    }
    if (filterType === 'rig') {
      const r = Object.keys(rigFilters).filter((k) => rigFilters[k]);
      if (r.length) lines.push({ label: 'Rigs', value: r.join(', ') });
    }
    if (yearFilter !== 'all') {
      lines.push({ label: 'Year', value: yearFilter });
    }
    if (dashboardSearchTerm) {
      lines.push({ label: 'Dashboard hole search', value: `"${dashboardSearchTerm}"` });
    }
    if (selectedProjectId && selectedProjectId !== 'all') {
      lines.push({ label: 'Campaign (dashboard)', value: String(selectedProjectId) });
    }
    return lines;
  }, [filterContext]);

  const tableRows = useMemo(() => {
    return data.map((row, index) => {
      const included = rowPassesVizFilters(row, holeFilters, coringSearchTerm);
      return {
        ...row,
        id: row.uid || String(row.dsr_activity_id) || `row-${index}`,
        included_in_viz: included,
        comments_display: row.comments
          ? String(row.comments).length > 48
            ? `${String(row.comments).slice(0, 48)}…`
            : row.comments
          : ''
      };
    });
  }, [data, holeFilters, coringSearchTerm]);

  const includedCount = useMemo(
    () => tableRows.filter((r) => r.included_in_viz).length,
    [tableRows]
  );

  const filteredCoringRows = useMemo(
    () => data.filter((row) => rowPassesVizFilters(row, holeFilters, coringSearchTerm)),
    [data, holeFilters, coringSearchTerm]
  );

  const handleExportCSV = useCallback(() => {
    if (!filteredCoringRows.length) return;
    const rows = filteredCoringRows.map(coringRowToCSVRecord);
    const datestamp = new Date().toISOString().split('T')[0];
    const period = dateRangeLabel === 'custom' && dateParams.start_date
      ? `${dateParams.start_date}_${dateParams.end_date || datestamp}`
      : dateRangeLabel;
    exportToCSV(`coring-data-${period}-${datestamp}.csv`, rows);
  }, [filteredCoringRows, dateRangeLabel, dateParams.start_date, dateParams.end_date]);

  const apiSummary = useMemo(() => {
    const hasStart = Boolean(dateParams.start_date);
    const hasEnd = Boolean(dateParams.end_date);
    if (!hasStart && !hasEnd) {
      return { label: 'API date filter', value: 'None (all time)' };
    }
    return {
      label: 'API date filter',
      value: `${dateParams.start_date || '…'} — ${dateParams.end_date || '…'} (inclusive)`
    };
  }, [dateParams]);

  const holeIds = filteredHoles.map((h) => h.hole_id).filter(Boolean);
  const holeChips = holeListExpanded ? holeIds : holeIds.slice(0, CHIP_MAX);

  if (loading) {
    return (
      <div className="coring-explorer-loading">
        <Spinner size="large" />
        <p>Loading v_coring data…</p>
      </div>
    );
  }

  if (error) {
    return (
      <Card variant="elevated" className="coring-explorer-error">
        <h3>Failed to load coring data</h3>
        <p>{error}</p>
      </Card>
    );
  }

  return (
    <div className="coring-data-explorer">
      <Card variant="elevated" style={{ padding: '1rem', marginBottom: '1rem' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '1rem'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '0.875rem', color: '#9ca3af' }}>Period (shared with Coring Activity):</span>
            <div style={{ display: 'flex', gap: '0.25rem', flexWrap: 'wrap' }}>
              {['week', 'month', 'year', 'all', 'custom'].map((range) => (
                <button
                  key={range}
                  type="button"
                  onClick={() => setDateRange?.(range)}
                  style={{
                    padding: '0.5rem 0.75rem',
                    borderRadius: '6px',
                    border: '1px solid',
                    borderColor: dateRangeLabel === range ? '#f9be0b' : '#374151',
                    background: dateRangeLabel === range ? 'rgba(249, 190, 11, 0.1)' : '#1f2937',
                    color: dateRangeLabel === range ? '#f9be0b' : '#9ca3af',
                    fontSize: '0.875rem',
                    fontWeight: dateRangeLabel === range ? '600' : '500',
                    cursor: 'pointer'
                  }}
                >
                  {range === 'all' ? 'All Time' : range}
                </button>
              ))}
            </div>
          </div>
          {dateRangeLabel === 'custom' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <input
                type="date"
                value={customStartDate}
                onChange={(e) => setCustomStartDate?.(e.target.value)}
                style={{
                  padding: '0.5rem',
                  background: '#1f2937',
                  border: '1px solid #374151',
                  borderRadius: '6px',
                  color: '#e5e7eb',
                  fontSize: '0.875rem'
                }}
              />
              <span style={{ color: '#9ca3af' }}>to</span>
              <input
                type="date"
                value={customEndDate}
                onChange={(e) => setCustomEndDate?.(e.target.value)}
                style={{
                  padding: '0.5rem',
                  background: '#1f2937',
                  border: '1px solid #374151',
                  borderRadius: '6px',
                  color: '#e5e7eb',
                  fontSize: '0.875rem'
                }}
              />
            </div>
          )}
          <input
            type="text"
            placeholder="Coring hole search…"
            value={coringSearchTerm}
            onChange={(e) => setSearchTerm?.(e.target.value)}
            style={{
              padding: '0.5rem 0.75rem',
              background: '#1f2937',
              border: '1px solid #374151',
              borderRadius: '6px',
              color: '#e5e7eb',
              fontSize: '0.875rem',
              minWidth: '200px'
            }}
          />
          <Button variant="ghost" size="sm" onClick={handleExportCSV} disabled={!filteredCoringRows.length}>
            Export CSV
          </Button>
        </div>
      </Card>

      <div className="coring-explorer-grid">
        <Card variant="elevated" className="coring-explorer-card">
          <h3 className="coring-explorer-card-title">API / krux.v_coring</h3>
          <dl className="coring-explorer-dl">
            <div>
              <dt>Source</dt>
              <dd>GET /api/coring_activity → krux.v_coring (data_deleted = N)</dd>
            </div>
            <div>
              <dt>Billable</dt>
              <dd>true (Y only)</dd>
            </div>
            <div>
              <dt>{apiSummary.label}</dt>
              <dd>{apiSummary.value}</dd>
            </div>
            <div>
              <dt>Period preset</dt>
              <dd>{dateRangeLabel}</dd>
            </div>
            <div>
              <dt>Rows returned</dt>
              <dd>{data.length}</dd>
            </div>
            <div>
              <dt>Rows in chart KPIs</dt>
              <dd>
                {includedCount} of {data.length} (hole + coring search filters)
              </dd>
            </div>
          </dl>
        </Card>

        <Card variant="elevated" className="coring-explorer-card">
          <h3 className="coring-explorer-card-title">Coring tab search</h3>
          <p className="coring-explorer-muted">
            {coringSearchTerm ? `Active: "${coringSearchTerm}"` : 'None — all holes from API pass search'}
          </p>
        </Card>

        <Card variant="elevated" className="coring-explorer-card">
          <h3 className="coring-explorer-card-title">Dashboard drill filters</h3>
          <dl className="coring-explorer-dl">
            {filterSummaryLines.map((line) => (
              <div key={line.label}>
                <dt>{line.label}</dt>
                <dd>{line.value}</dd>
              </div>
            ))}
          </dl>
        </Card>

        <Card variant="elevated" className="coring-explorer-card coring-explorer-card-span">
          <h3 className="coring-explorer-card-title">
            Active hole scope ({holeIds.length} holes)
          </h3>
          <p className="coring-explorer-muted">
            Coring rows are included in charts only when hole_name matches one of these hole IDs (after
            normalization).
          </p>
          <div className="coring-explorer-chips">
            {holeChips.map((id) => (
              <span key={id} className="coring-explorer-chip">
                {id}
              </span>
            ))}
            {holeIds.length > CHIP_MAX && (
              <button
                type="button"
                className="coring-explorer-chip-toggle"
                onClick={() => setHoleListExpanded(!holeListExpanded)}
              >
                {holeListExpanded ? 'Show fewer' : `+${holeIds.length - CHIP_MAX} more`}
              </button>
            )}
          </div>
        </Card>
      </div>

      {filteredHoles.length > 0 && (
        <Card variant="elevated" className="coring-explorer-card" style={{ marginBottom: '1rem' }}>
          <h3 className="coring-explorer-card-title">Holes in scope (detail)</h3>
          <div className="coring-explorer-hole-table-wrap">
            <table className="coring-explorer-mini-table">
              <thead>
                <tr>
                  <th>hole_id</th>
                  <th>prospect</th>
                  <th>drill_rig</th>
                  <th>status</th>
                </tr>
              </thead>
              <tbody>
                {filteredHoles.map((h) => (
                  <tr key={h.hole_id}>
                    <td>{h.hole_id}</td>
                    <td>{h.prospect || '—'}</td>
                    <td>{h.drill_rig || '—'}</td>
                    <td>{h.hole_status || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <Card variant="elevated" className="coring-explorer-table-card">
        <h3 className="coring-explorer-card-title">Raw API rows (v_coring fields)</h3>
        <div className="coring-explorer-table-scroll">
          <Table
            data={tableRows}
            loading={false}
            striped
            compact
            columns={[
              {
                key: 'included_in_viz',
                title: 'In chart KPIs',
                width: '110px',
                render: (v) => (
                  <span className={v ? 'coring-explorer-badge yes' : 'coring-explorer-badge no'}>
                    {v ? 'Yes' : 'No'}
                  </span>
                )
              },
              { key: 'report_date', title: 'report_date' },
              { key: 'drill', title: 'drill' },
              { key: 'shift', title: 'shift' },
              { key: 'hole_name', title: 'hole_name' },
              { key: 'hole_id', title: 'hole_id' },
              { key: 'activity', title: 'activity' },
              { key: 'activity_type', title: 'activity_type' },
              {
                key: 'penetration',
                title: 'penetration',
                render: (v) => (v != null ? Number(v).toFixed(1) : '—')
              },
              {
                key: 'activity_hours',
                title: 'activity_hours',
                render: (v) => (v != null ? Number(v).toFixed(2) : '—')
              },
              { key: 'billable', title: 'billable' },
              { key: 'comments_display', title: 'comments' }
            ]}
            emptyMessage="No rows for the selected API filters."
          />
        </div>
      </Card>
    </div>
  );
};

export default CoringDataExplorer;
